import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Loader2, FileText, User, Building, Tag, ShieldCheck } from "lucide-react";
import { QuotationReceiptModal } from "./QuotationReceiptModal";
import { useCustomerLevels, CUSTOMER_LEVELS_FALLBACK } from "@/hooks/useCustomerLevels";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const QuotationModal = ({ open, onClose, cart, calculateTotals, priceLevel, onComplete }) => {
  // Mirror Sales.jsx customer-discount logic so quotes match invoices.
  const { map: CUSTOMER_LEVEL_DISCOUNTS } = useCustomerLevels();
  const getCustomerDiscountPercent = (cust) => {
    if (!cust) return 0;
    const lvl = (CUSTOMER_LEVEL_DISCOUNTS[cust.customer_level] ?? CUSTOMER_LEVELS_FALLBACK[cust.customer_level]) || 0;
    const extra = Number(cust.discount_percent) || 0;
    return lvl + extra;
  };
  const [quoteType, setQuoteType] = useState("CASH QUOTE");
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState("");
  const [processing, setProcessing] = useState(false);
  const [completedQuotation, setCompletedQuotation] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [validDays, setValidDays] = useState(30);
  const [notes, setNotes] = useState("");
  // User-controlled override: when true, the customer's automatic
  // level/override discount is NOT applied to this specific quote.
  // Manual per-line discounts remain untouched.
  const [disableAutoDiscount, setDisableAutoDiscount] = useState(false);
  
  // Cash customer details (for new customers on cash quotes)
  const [cashCustomerName, setCashCustomerName] = useState("");
  const [cashCustomerAddress, setCashCustomerAddress] = useState("");
  const [cashCustomerSuburb, setCashCustomerSuburb] = useState("");
  const [cashCustomerProvince, setCashCustomerProvince] = useState("");
  const [cashCustomerContact, setCashCustomerContact] = useState("");
  const [cashCustomerEmail, setCashCustomerEmail] = useState("");
  const [cashCustomerVat, setCashCustomerVat] = useState("");

  const { subtotal: rawSubtotal, tax: rawTax, total: rawTotal } = calculateTotals();

  // Customer-level discount pulled from the selected account customer.
  // Applied per-line (except items flagged `no_discount`) and shown on the
  // quote. Users can veto it for a single quote via `disableAutoDiscount`.
  const rawCustomerDiscountPercent = useMemo(() => {
    if (quoteType !== "ACCOUNT QUOTE" || !selectedCustomer) return 0;
    return getCustomerDiscountPercent(selectedCustomer);
  }, [quoteType, selectedCustomer]);
  const customerDiscountPercent = disableAutoDiscount ? 0 : rawCustomerDiscountPercent;

  // ─── VAT-Exemption Re-Pricing ─────────────────────────────────────────
  // Mirror Sales.jsx `repriceCartForCustomer`: when the selected customer
  // holds a VAT-exemption certificate AND a line's product is marked
  // "Exempt" / "VAT Exempt Eligible", swap the shelf price for either:
  //   • the product's dedicated VAT-Exempt Selling Price (stripped /1.15)
  //   • or, if none captured, the shelf /1.15
  // So the quote shows the *actual* price the exempt customer will pay.
  const customerIsVatExempt = !!selectedCustomer?.has_vat_exemption;

  const vatAdjustedCart = useMemo(() => {
    return cart.map((item) => {
      const shelfPrice = Number(item._shelf_unit_price ?? item.unit_price) || 0;
      const cat = item.vat_category || "Standard 15%";
      const isExemptCat = (cat === "Exempt" || cat === "VAT Exempt Eligible");
      const specialPrice = Number(item.vat_exempt_price_incl_vat || 0);
      const qty = Number(item.quantity) || 0;
      const manualDisc = Number(item.discount_percent) || 0;

      let newUnit = shelfPrice;
      let vatRate = null; // fall back to backend default (15%) unless stripped
      let vatExemptApplied = false;
      let originalUnitPrice = null;
      let originalTotal = null;

      if (customerIsVatExempt && isExemptCat) {
        originalUnitPrice = shelfPrice;
        originalTotal = shelfPrice * qty * (1 - manualDisc / 100);
        if (specialPrice > 0) {
          newUnit = Math.round((specialPrice / 1.15) * 100) / 100;
        } else {
          newUnit = Math.round((shelfPrice / 1.15) * 100) / 100;
        }
        vatRate = 0;
        vatExemptApplied = true;
      }

      const newTotal = Math.round(newUnit * qty * (1 - manualDisc / 100) * 100) / 100;
      return {
        ...item,
        _shelf_unit_price: shelfPrice,
        unit_price: newUnit,
        total: newTotal,
        vat_rate: vatRate,
        vat_exemption_applied: vatExemptApplied,
        original_unit_price: originalUnitPrice,
        original_total: originalTotal,
      };
    });
  }, [cart, customerIsVatExempt]);

  // Cart re-priced with the customer discount folded in. Takes the higher
  // of manual per-line discount and the customer's %, applied on the
  // already VAT-adjusted unit price.
  const effectiveCart = useMemo(() => {
    if (customerDiscountPercent <= 0) return vatAdjustedCart;
    return vatAdjustedCart.map((item) => {
      if (item.no_discount) return item;
      const manualPct = Number(item.discount_percent) || 0;
      const effectivePct = Math.max(manualPct, customerDiscountPercent);
      if (effectivePct === manualPct) return item;
      const gross = (Number(item.unit_price) || 0) * (Number(item.quantity) || 0);
      const total = gross - (gross * effectivePct) / 100;
      // Refresh original_total to reflect the new customer % if this line
      // is VAT-exempt (so the audit trail stays consistent).
      let originalTotal = item.original_total;
      if (item.vat_exemption_applied && item.original_unit_price != null) {
        originalTotal = Math.round(item.original_unit_price * item.quantity * (1 - effectivePct / 100) * 100) / 100;
      }
      return {
        ...item,
        discount_percent: effectivePct,
        total: Math.round(total * 100) / 100,
        original_total: originalTotal,
      };
    });
  }, [vatAdjustedCart, customerDiscountPercent]);

  // ─── Mixed-VAT totals ──────────────────────────────────────────────────
  // Standard-rated lines: `total` is incl-VAT → subtotal contribution = total/1.15
  // Exempt lines (vat_rate=0): `total` is ex-VAT → subtotal contribution = total
  const { subtotal, tax, total, totalDiscount } = useMemo(() => {
    let subEx = 0;
    let vat = 0;
    let ttl = 0;
    let disc = 0;
    for (const item of effectiveCart) {
      const lineTotal = Number(item.total) || 0;
      ttl += lineTotal;
      const isExempt = item.vat_exemption_applied || item.vat_rate === 0;
      if (isExempt) {
        subEx += lineTotal;
      } else {
        const sub = lineTotal / 1.15;
        subEx += sub;
        vat += lineTotal - sub;
      }
      const shelf = Number(item._shelf_unit_price ?? item.unit_price) || 0;
      const qty = Number(item.quantity) || 0;
      const pct = Number(item.discount_percent) || 0;
      disc += (shelf * qty * pct) / 100;
    }
    return {
      subtotal: subEx,
      tax: vat,
      total: Math.round(ttl * 100) / 100,
      totalDiscount: disc,
    };
  }, [effectiveCart]);
  const roundedTotal = total;
  // rawSubtotal / rawTax / rawTotal from the parent cart (before customer
  // pick) are only used when the modal renders BEFORE any customer is
  // selected — kept for backwards compat but the memoised values above
  // always take precedence.
  void rawSubtotal; void rawTax; void rawTotal;

  useEffect(() => {
    if (open) {
      fetchCustomers();
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      // Reset form when modal closes
      setQuoteType("CASH QUOTE");
      setSelectedCustomer(null);
      setCustomerSearch("");
      setValidDays(30);
      setNotes("");
      setDisableAutoDiscount(false);
      setCashCustomerName("");
      setCashCustomerAddress("");
      setCashCustomerSuburb("");
      setCashCustomerProvince("");
      setCashCustomerContact("");
      setCashCustomerEmail("");
      setCashCustomerVat("");
    }
  }, [open]);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`, {
        params: {
          include_calculated_balance: true,  // Use calculated balance for accurate display
          limit: 2000,
        }
      });
      // Sort customers alphabetically by name
      const sortedCustomers = (response.data || []).sort((a, b) => 
        (a.name || '').localeCompare(b.name || '')
      );
      setCustomers(sortedCustomers);
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

  const filteredCustomers = customers.filter(c => 
    c.name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
    c.account_number?.toLowerCase().includes(customerSearch.toLowerCase())
  );

  const handleCreateQuotation = async () => {
    if (cart.length === 0) {
      toast.error("Cart is empty");
      return;
    }

    // For account quotes, require customer selection
    if (quoteType === "ACCOUNT QUOTE" && !selectedCustomer) {
      toast.error("Please select a customer for account quote");
      return;
    }

    setProcessing(true);

    try {
      const quotationData = {
        quote_type: quoteType,
        customer_id: quoteType === "ACCOUNT QUOTE" ? selectedCustomer?.id : null,
        customer_name: quoteType === "ACCOUNT QUOTE" ? selectedCustomer?.name : (cashCustomerName || null),
        customer_account_number: quoteType === "ACCOUNT QUOTE" ? selectedCustomer?.account_number : null,
        customer_address: quoteType === "ACCOUNT QUOTE" ? selectedCustomer?.street_address : (cashCustomerAddress || null),
        customer_suburb: quoteType === "ACCOUNT QUOTE" ? selectedCustomer?.suburb : (cashCustomerSuburb || null),
        customer_province: quoteType === "ACCOUNT QUOTE" ? selectedCustomer?.province : (cashCustomerProvince || null),
        customer_contact: quoteType === "ACCOUNT QUOTE" ? selectedCustomer?.contact_number : (cashCustomerContact || null),
        customer_email: quoteType === "ACCOUNT QUOTE" ? selectedCustomer?.email : (cashCustomerEmail || null),
        customer_vat_number: quoteType === "ACCOUNT QUOTE" ? selectedCustomer?.vat_number : (cashCustomerVat || null),
        items: effectiveCart,
        subtotal: subtotal,
        discount: Math.round(totalDiscount * 100) / 100,
        tax: tax,
        total: roundedTotal,
        valid_days: validDays,
        notes: notes || null,
        created_by: "Cashier 1",
        price_level: priceLevel,
        customer_discount_percent: customerDiscountPercent,
      };

      const response = await axios.post(`${API}/quotations`, quotationData);
      
      toast.success(`Quotation ${response.data.quotation_number} created successfully!`);
      setCompletedQuotation(response.data);
      setShowReceipt(true);
    } catch (error) {
      console.error("Error creating quotation:", error);
      toast.error(error.response?.data?.detail || "Failed to create quotation");
    } finally {
      setProcessing(false);
    }
  };

  const handleReceiptClose = () => {
    setShowReceipt(false);
    setCompletedQuotation(null);
    onComplete();
  };

  // Show receipt modal after quotation created
  if (showReceipt && completedQuotation) {
    return (
      <QuotationReceiptModal
        open={showReceipt}
        onClose={handleReceiptClose}
        quotation={completedQuotation}
      />
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="quotation-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <FileText className="h-6 w-6 text-blue-600" />
            Create Quotation
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-6 py-4">
          {/* Left Column - Quote Type & Customer */}
          <div className="space-y-4">
            {/* Quote Type Selection */}
            <div>
              <Label className="text-lg mb-3 block">Quote Type</Label>
              <div className="grid grid-cols-2 gap-3">
                <Button
                  type="button"
                  variant={quoteType === "CASH QUOTE" ? "default" : "outline"}
                  onClick={() => {
                    setQuoteType("CASH QUOTE");
                    setSelectedCustomer(null);
                  }}
                  className={`h-14 text-lg ${quoteType === "CASH QUOTE" ? "bg-blue-600 hover:bg-blue-700" : ""}`}
                  data-testid="quote-type-cash"
                >
                  <User className="mr-2 h-5 w-5" />
                  CASH QUOTE
                </Button>
                <Button
                  type="button"
                  variant={quoteType === "ACCOUNT QUOTE" ? "default" : "outline"}
                  onClick={() => setQuoteType("ACCOUNT QUOTE")}
                  className={`h-14 text-lg ${quoteType === "ACCOUNT QUOTE" ? "bg-blue-600 hover:bg-blue-700" : ""}`}
                  data-testid="quote-type-account"
                >
                  <Building className="mr-2 h-5 w-5" />
                  ACCOUNT QUOTE
                </Button>
              </div>
            </div>

            {/* Account Customer Selection */}
            {quoteType === "ACCOUNT QUOTE" && (
              <div className="border rounded-lg p-4 bg-gray-50">
                <Label className="mb-2 block font-semibold">Select Customer</Label>
                <Input
                  placeholder="Search customers..."
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  className="mb-3"
                  data-testid="quote-customer-search"
                />
                <div className="max-h-48 overflow-y-auto space-y-2">
                  {filteredCustomers.map((customer) => {
                    const disc = getCustomerDiscountPercent(customer);
                    return (
                      <div
                        key={customer.id}
                        onClick={() => setSelectedCustomer(customer)}
                        className={`p-3 rounded cursor-pointer border ${
                          selectedCustomer?.id === customer.id
                            ? "bg-blue-100 border-blue-500"
                            : "bg-white hover:bg-gray-100"
                        }`}
                        data-testid={`quote-customer-option-${customer.id}`}
                      >
                        <div className="flex justify-between items-center">
                          <div className="font-semibold">{customer.name}</div>
                          {disc > 0 && (
                            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700 flex items-center gap-1">
                              <Tag className="h-3 w-3" />
                              {disc}% OFF
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500">
                          {customer.account_number} • {customer.contact_number}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {selectedCustomer && (
                  <div className="mt-3 p-3 bg-blue-50 rounded border border-blue-200" data-testid="quote-selected-customer">
                    <div className="font-semibold text-blue-800">Selected: {selectedCustomer.name}</div>
                    <div className="text-sm text-blue-600">{selectedCustomer.account_number}</div>
                    {rawCustomerDiscountPercent > 0 ? (
                      <>
                        <div
                          className="mt-2 text-sm font-semibold text-green-700 flex items-center gap-1"
                          data-testid="quote-customer-discount-applied"
                        >
                          <Tag className="h-4 w-4" />
                          {disableAutoDiscount
                            ? `${rawCustomerDiscountPercent}% customer discount is CANCELLED on this quote`
                            : `Applying ${rawCustomerDiscountPercent}% customer discount to all eligible items`}
                        </div>
                        <label
                          className="mt-2 flex items-center gap-2 text-xs text-gray-700 cursor-pointer select-none"
                          data-testid="quote-cancel-auto-discount-label"
                        >
                          <Checkbox
                            checked={disableAutoDiscount}
                            onCheckedChange={(v) => setDisableAutoDiscount(!!v)}
                            data-testid="quote-cancel-auto-discount"
                          />
                          <span>Cancel the standard {rawCustomerDiscountPercent}% discount on this quote</span>
                        </label>
                      </>
                    ) : (
                      <div className="mt-2 text-xs text-gray-500">
                        This customer has no automatic discount
                      </div>
                    )}
                    {customerIsVatExempt && (
                      <div
                        className="mt-2 text-sm font-semibold text-emerald-700 flex items-center gap-1"
                        data-testid="quote-vat-exempt-applied"
                      >
                        <ShieldCheck className="h-4 w-4" />
                        VAT-exempt customer — exempt-eligible lines priced ex-VAT
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Cash Customer Details Form */}
            {quoteType === "CASH QUOTE" && (
              <div className="border rounded-lg p-4 bg-blue-50">
                <Label className="mb-3 block font-semibold text-blue-800">
                  Customer Details (Optional)
                </Label>
                <p className="text-sm text-blue-600 mb-3">
                  Enter details if customer wants their info on the quote
                </p>
                <div className="space-y-3">
                  <Input
                    placeholder="Customer Name"
                    value={cashCustomerName}
                    onChange={(e) => setCashCustomerName(e.target.value)}
                  />
                  <Input
                    placeholder="Contact Number"
                    value={cashCustomerContact}
                    onChange={(e) => setCashCustomerContact(e.target.value)}
                  />
                  <Input
                    placeholder="Email Address"
                    value={cashCustomerEmail}
                    onChange={(e) => setCashCustomerEmail(e.target.value)}
                  />
                  <Input
                    placeholder="Street Address"
                    value={cashCustomerAddress}
                    onChange={(e) => setCashCustomerAddress(e.target.value)}
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      placeholder="Suburb"
                      value={cashCustomerSuburb}
                      onChange={(e) => setCashCustomerSuburb(e.target.value)}
                    />
                    <Input
                      placeholder="Province"
                      value={cashCustomerProvince}
                      onChange={(e) => setCashCustomerProvince(e.target.value)}
                    />
                  </div>
                  <Input
                    placeholder="VAT Number (if applicable)"
                    value={cashCustomerVat}
                    onChange={(e) => setCashCustomerVat(e.target.value)}
                  />
                </div>
              </div>
            )}

            {/* Quote Settings */}
            <div className="border rounded-lg p-4">
              <Label className="mb-3 block font-semibold">Quote Settings</Label>
              <div className="space-y-3">
                <div>
                  <Label className="text-sm mb-1 block">Valid for (days)</Label>
                  <Input
                    type="number"
                    value={validDays}
                    onChange={(e) => setValidDays(parseInt(e.target.value) || 30)}
                    min={1}
                    max={365}
                  />
                </div>
                <div>
                  <Label className="text-sm mb-1 block">Notes / Terms</Label>
                  <Textarea
                    placeholder="Enter any notes or special terms..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="min-h-[60px]"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Cart Summary & Total */}
          <div className="space-y-4">
            {/* Cart Items */}
            <div className="border rounded-lg p-4 max-h-80 overflow-y-auto">
              <Label className="mb-3 block font-semibold">Quote Items ({effectiveCart.length})</Label>
              {effectiveCart.map((item, index) => {
                const shelfUnit = Number(item._shelf_unit_price ?? item.unit_price) || 0;
                const gross = shelfUnit * (item.quantity || 0);
                const discPct = item.discount_percent || 0;
                const isVatExemptLine = !!item.vat_exemption_applied;
                return (
                  <div key={index} className="flex justify-between items-center py-2 border-b last:border-0">
                    <div>
                      <div className="font-medium flex items-center gap-2">
                        {item.name}
                        {isVatExemptLine && (
                          <span
                            className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700"
                            data-testid={`quote-line-vat-exempt-${index}`}
                          >
                            EX-VAT
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500">
                        {item.quantity} x R {item.unit_price?.toFixed(2)}
                        {discPct > 0 && (
                          <span className="ml-2 text-green-700 font-semibold">
                            −{discPct}%
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      {(discPct > 0 || isVatExemptLine) && (
                        <div className="text-xs text-gray-400 line-through">
                          R {gross.toFixed(2)}
                        </div>
                      )}
                      <div className="font-semibold">R {item.total?.toFixed(2)}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Totals */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <div className="space-y-2">
                <div className="flex justify-between text-lg">
                  <span>Subtotal (Excl VAT):</span>
                  <span>R {subtotal.toFixed(2)}</span>
                </div>
                {totalDiscount > 0 && (
                  <div className="flex justify-between text-lg text-red-600" data-testid="quote-totals-discount">
                    <span>
                      Discount{customerDiscountPercent > 0 ? ` (${customerDiscountPercent}% customer)` : ''}:
                    </span>
                    <span>-R {totalDiscount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg">
                  <span>VAT (15%):</span>
                  <span>R {tax.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-2xl font-bold border-t pt-3 mt-2">
                  <span>Total:</span>
                  <span className="text-blue-600">R {roundedTotal.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={onClose}
                className="flex-1 h-14 text-lg"
                disabled={processing}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateQuotation}
                className="flex-1 h-14 text-lg bg-blue-600 hover:bg-blue-700"
                disabled={processing || (quoteType === "ACCOUNT QUOTE" && !selectedCustomer)}
                data-testid="create-quotation-button"
              >
                {processing ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <FileText className="mr-2 h-5 w-5" />
                    CREATE QUOTE
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QuotationModal;
